import { useState } from 'react';
import type { SavedAction } from '@/types';
import { PinIcon } from '@/components/icons';
import { EditSavedTaskModal } from './EditSavedTaskModal';
import { renderWithMentions } from '@/utils/renderMentions';

interface SavedActionsProps {
  actions: SavedAction[];
  onSave: (action: SavedAction) => void;
  onRemove: (id: string) => void;
  onTogglePin?: (id: string) => void;
  onApplyTemplate?: (text: string) => void;
}

export function SavedActions({ actions, onSave, onRemove, onTogglePin, onApplyTemplate }: SavedActionsProps) {
  const [editingAction, setEditingAction] = useState<SavedAction | null>(null);

  if (actions.length === 0) {
    return (
      <div className="section-panel">
        <h3 className="section-panel__title">Saved actions</h3>
        <div className="empty-state">
          <div className="empty-state__icon-circle" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              focusable="false"
            >
              <path
                d="M12 14.083v5.834M9.667 7.829v1.776c0 .173 0 .26-.017.343a.793.793 0 0 1-.062.187 1.81 1.81 0 0 1-.197.232L8.082 12c-.555.693-.832 1.04-.832 1.332 0 .254.115.494.314.652.228.182.672.182 1.56.182h5.785c.888 0 1.332 0 1.56-.182a.83.83 0 0 0 .314-.652c0-.292-.278-.638-.832-1.332l-1.309-1.633a1.81 1.81 0 0 1-.196-.232.79.79 0 0 1-.063-.187 1.91 1.91 0 0 1-.016-.343V7.829c0-.096 0-.144.005-.191a.95.95 0 0 1 .031-.157 1.95 1.95 0 0 1 .062-.169l.84-2.099c.245-.612.367-.918.316-1.164a.752.752 0 0 0-.345-.5c-.21-.139-.539-.139-1.199-.139H9.923c-.66 0-.99 0-1.199.139a.752.752 0 0 0-.345.5c-.051.246.072.552.317 1.164l.84 2.099c.035.089.053.134.066.18.011.041.019.083.024.126.005.047.005.095.005.191Z"
                stroke="#ABAB9C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="empty-state__title">Save your frequently used tasks</p>
          <p className="empty-state__desc">
            When you hover over a chat or starter prompt, you can pin it here for quick access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-panel">
      <h3 className="section-panel__title">Saved actions</h3>
      <p className="section-panel__subtitle">
        Click a card to load it into the chat box, then send with the arrow. Use the pin to keep favorites at the top.
      </p>
      <div className="saved-actions__list">
        {actions.map((action) => (
          <div
            key={action.id}
            className={`saved-action-card${action.isPinned ? ' saved-action-card--pinned' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onApplyTemplate?.(action.template)}
            onKeyDown={(e) => e.key === 'Enter' && onApplyTemplate?.(action.template)}
          >
            <button
              className={`saved-action-card__pin${action.isPinned ? ' saved-action-card__pin--filled' : ''}`}
              type="button"
              title={action.isPinned ? 'Unpin' : 'Pin to top'}
              aria-pressed={action.isPinned}
              onClick={(e) => { e.stopPropagation(); onTogglePin?.(action.id); }}
              aria-label={action.isPinned ? 'Unpin action' : 'Pin action'}
            >
              <PinIcon filled={action.isPinned} />
            </button>
            <p className="saved-action-card__text">{renderWithMentions(action.template)}</p>
            <button
              className="saved-action-card__edit"
              type="button"
              onClick={(e) => { e.stopPropagation(); setEditingAction(action); }}
              aria-label="Edit saved action"
            >
              <svg width="18" height="18" viewBox="0 0 13 13" fill="none">
                <path
                  d="M9 2L11 4L5 10H3V8L9 2Z"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {editingAction && (
        <EditSavedTaskModal
          action={editingAction}
          onSave={(a) => { onSave(a); setEditingAction(null); }}
          onRemove={(id) => { onRemove(id); setEditingAction(null); }}
          onClose={() => setEditingAction(null)}
        />
      )}
    </div>
  );
}
